import { useState, useEffect, useCallback, useRef } from "react";
import "./index.css";
import type { SiteProfile, WeatherData, ScoreResult } from "./types";
import { colors, fonts } from "./tokens";
import { DEMO_PROFILES } from "./profiles";
import { fetchWeather, FALLBACK_WEATHER } from "./weather";
import type { GeoResult } from "./weather";
import { computeScore } from "./scoring";
import { ProfileEditor, DailyInput, ScoreCard, RecsPanel } from "./components/Panels";
import { Dashboard } from "./components/Dashboard";
import type { ProjectScore } from "./components/Dashboard";
import { LocationInput } from "./components/LocationInput";

type View = "dashboard" | "project" | "adhoc";
type Role = "gc" | "sub";

function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

export default function App() {
  const [view, setView] = useState<View>("dashboard");
  const [role, setRole] = useState<Role>("gc");
  const [profiles, setProfiles] = useState<SiteProfile[]>(() =>
    DEMO_PROFILES.map(deepClone),
  );
  const [selIdx, setSelIdx] = useState(0);
  const [allScores, setAllScores] = useState<(ProjectScore | null)[]>(() =>
    profiles.map(() => null),
  );
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevScoreRef = useRef<number | null>(null);
  const [scoreDelta, setScoreDelta] = useState<number | null>(null);
  const profilesRef = useRef(profiles);
  profilesRef.current = profiles;

  // Ad-hoc state
  const [adhocResult, setAdhocResult] = useState<ScoreResult | null>(null);
  const [adhocWeather, setAdhocWeather] = useState<WeatherData | null>(null);
  const [adhocWxSource, setAdhocWxSource] = useState("—");
  const [adhocLabel, setAdhocLabel] = useState("");

  const profile = profiles[selIdx];
  const currentScore = allScores[selIdx];

  // Score all projects on mount
  useEffect(() => {
    let cancelled = false;
    async function scoreAll() {
      setLoading(true);
      const results = await Promise.all(
        profilesRef.current.map(async (p) => {
          let wx: WeatherData;
          let src: string;
          try {
            wx = await fetchWeather(p.latitude, p.longitude);
            src = "live · Open-Meteo";
          } catch {
            wx = FALLBACK_WEATHER;
            src = "unavailable · baseline";
          }
          return { result: computeScore(p, wx), weather: wx, wxSource: src };
        }),
      );
      if (!cancelled) {
        setAllScores(results);
        setLoading(false);
      }
    }
    scoreAll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track score delta for current project
  useEffect(() => {
    if (!currentScore) return;
    const rounded = Math.round(currentScore.result.overall);
    if (prevScoreRef.current !== null) {
      const delta = rounded - prevScoreRef.current;
      if (delta !== 0) {
        setScoreDelta(delta);
        const t = setTimeout(() => setScoreDelta(null), 2000);
        prevScoreRef.current = rounded;
        return () => clearTimeout(t);
      }
    }
    prevScoreRef.current = rounded;
  }, [currentScore]);

  // Reset delta when switching projects
  useEffect(() => {
    prevScoreRef.current = null;
    setScoreDelta(null);
  }, [selIdx]);

  // Update a nested field in the current profile
  const updateProfile = useCallback(
    (path: string, value: unknown) => {
      setProfiles((prev) => {
        const next = prev.map((p, i) => (i !== selIdx ? p : deepClone(p)));
        const parts = path.split(".");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let obj: any = next[selIdx];
        for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
        obj[parts[parts.length - 1]] = value;
        return next;
      });
    },
    [selIdx],
  );

  // Rescore current project when profile changes (debounced)
  const rescoreProject = useCallback((idx: number) => {
    setAllScores((prev) => {
      const ps = prev[idx];
      if (!ps) return prev;
      const newResult = computeScore(profilesRef.current[idx], ps.weather);
      return prev.map((s, i) => (i === idx ? { ...ps, result: newResult } : s));
    });
  }, []);

  useEffect(() => {
    if (view !== "project") return;
    if (!allScores[selIdx]) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => rescoreProject(selIdx), 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // profiles triggers rescore; allScores intentionally excluded to prevent loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, selIdx, view, rescoreProject]);

  // Navigate to project detail
  const openProject = (idx: number) => {
    setSelIdx(idx);
    setView("project");
    prevScoreRef.current = null;
    setScoreDelta(null);
  };

  // Handle ad-hoc location
  const handleAdHoc = async (geo: GeoResult) => {
    const defaultProfile: SiteProfile = {
      project_id: "adhoc",
      project_name: "Ad-Hoc Score",
      location_name: geo.short,
      latitude: geo.lat,
      longitude: geo.lng,
      schedule_phases: ["mobilization"],
      concurrent: 0,
      pressure: 0,
      workforce: { total_workers: 0, new_workers_under_90_days: 0, trades_on_site: 1, overtime_hours_this_week: 0, night_shift: false },
      compliance: { open_violations: 0, violations_last_7_days: 0, violations_last_30_days: 0, ppe_compliance_rate: 100, days_since_last_incident: 365, toolbox_talk_completed_today: false },
      equipment: { cranes_active: 0, heavy_equipment_count: 0, man_lifts_active: 0, concrete_pumps_active: 0, critical_lifts_planned: false },
    };
    let wx: WeatherData;
    let src: string;
    try {
      wx = await fetchWeather(geo.lat, geo.lng);
      src = "live · Open-Meteo";
    } catch {
      wx = FALLBACK_WEATHER;
      src = "unavailable · baseline";
    }
    setAdhocWeather(wx);
    setAdhocWxSource(src);
    setAdhocLabel(geo.short);
    setAdhocResult(computeScore(defaultProfile, wx));
  };

  const dateStr = new Date()
    .toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    })
    .toUpperCase();

  const backButton = (
    <button
      onClick={() => setView("dashboard")}
      style={{
        background: "none", border: `1px solid ${colors.border}`,
        borderRadius: 4, padding: "4px 12px",
        color: colors.text2, fontSize: 10, fontFamily: fonts.mono,
        cursor: "pointer", letterSpacing: "0.06em",
      }}
    >
      ← DASHBOARD
    </button>
  );

  return (
    <div style={{ minHeight: "100vh", background: colors.bg, color: colors.text }}>
      {/* Header */}
      <div
        style={{
          padding: "12px 20px",
          borderBottom: `1px solid ${colors.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: colors.surface,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <a href="#" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 12, color: colors.muted }}>&larr;</span>
            <span style={{ fontFamily: fonts.mono, fontWeight: 700, fontSize: 15, letterSpacing: "0.12em", color: colors.text }}>
              CARDO<span style={{ color: colors.amber }}>.</span>
            </span>
          </a>
          <span style={{ fontSize: 11, color: colors.muted, borderLeft: `1px solid ${colors.border}`, paddingLeft: 14 }}>
            Site Safety Intelligence
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontFamily: fonts.mono, fontSize: 10, color: colors.muted, letterSpacing: "0.06em" }}>{dateStr}</span>
          {/* Role toggle */}
          <div style={{ display: "flex", gap: 3 }}>
            {(["gc", "sub"] as const).map((r) => (
              <button
                key={r}
                onClick={() => setRole(r)}
                style={{
                  padding: "4px 12px",
                  fontSize: 9,
                  fontFamily: fonts.mono,
                  letterSpacing: "0.08em",
                  background: role === r ? colors.amberGlow : "transparent",
                  color: role === r ? colors.amber : colors.muted,
                  border: `1px solid ${role === r ? colors.amber + "44" : colors.border}`,
                  borderRadius: 3,
                  cursor: "pointer",
                }}
              >
                {r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1120, margin: "0 auto", padding: "16px 20px" }}>
        {/* Demo context banner */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px", marginBottom: 12,
          background: colors.amberGlow, border: `1px solid ${colors.amber}33`,
          borderRadius: 6, fontSize: 11, color: colors.text2,
        }}>
          <span style={{ fontFamily: fonts.mono, fontSize: 9, fontWeight: 700, color: colors.amber, letterSpacing: "0.08em" }}>DEMO</span>
          <span>
            {role === "gc"
              ? "GC Safety Director — Monitor all projects, configure risk parameters, and review recommendations."
              : "Subcontractor — Daily check-in with crew data and your team's action items."}
          </span>
        </div>

        {/* ── DASHBOARD VIEW ── */}
        {view === "dashboard" && (
          <>
            {loading && allScores.every((s) => s === null) ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <span style={{ fontFamily: fonts.mono, fontSize: 11, color: colors.muted, letterSpacing: "0.1em", animation: "pulse 1.5s infinite" }}>
                  SCORING ALL PROJECTS...
                </span>
              </div>
            ) : (
              <>
                <Dashboard profiles={profiles} scores={allScores} onSelect={openProject} role={role} />

                {/* Ad-hoc entry point */}
                <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
                  <button
                    onClick={() => setView("adhoc")}
                    style={{
                      padding: "6px 16px", fontSize: 10, fontFamily: fonts.mono,
                      letterSpacing: "0.06em", background: "transparent",
                      color: colors.text2, border: `1px solid ${colors.border}`,
                      borderRadius: 4, cursor: "pointer",
                    }}
                  >
                    AD-HOC SCORE →
                  </button>
                  <span style={{ fontSize: 10, color: colors.muted }}>
                    Score any location by weather alone
                  </span>
                </div>
              </>
            )}
          </>
        )}

        {/* ── PROJECT DETAIL VIEW ── */}
        {view === "project" && currentScore && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              {backButton}
              <div>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{profile.project_name}</span>
                <span style={{ fontSize: 11, color: colors.muted, marginLeft: 8 }}>{profile.location_name}</span>
              </div>
            </div>

            <div
              className="demo-grid"
              style={{
                display: "grid",
                gridTemplateColumns: role === "gc" ? "280px 300px 1fr" : "320px 1fr",
                gap: 14,
                alignItems: "start",
              }}
            >
              {/* LEFT: Editor (GC) or Daily Input (Sub) */}
              {role === "gc" ? (
                <ProfileEditor profile={profile} onUpdate={updateProfile} />
              ) : (
                <DailyInput profile={profile} onUpdate={updateProfile} />
              )}

              {/* CENTER (GC) or RIGHT (Sub): Score + optionally recs */}
              {role === "sub" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <ScoreCard result={currentScore.result} weather={currentScore.weather}
                    wxSource={currentScore.wxSource} locLabel={profile.location_name}
                    dateStr={dateStr} delta={scoreDelta} />
                  <RecsPanel sif={currentScore.result.sif} recs={currentScore.result.recs}
                    title="Your Recommendations" />
                </div>
              ) : (
                <>
                  <ScoreCard result={currentScore.result} weather={currentScore.weather}
                    wxSource={currentScore.wxSource} locLabel={profile.location_name}
                    dateStr={dateStr} delta={scoreDelta} />
                  <RecsPanel sif={currentScore.result.sif} recs={currentScore.result.recs} />
                </>
              )}
            </div>
          </>
        )}

        {/* ── AD-HOC VIEW ── */}
        {view === "adhoc" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              {backButton}
              <span style={{ fontWeight: 600, fontSize: 14 }}>Ad-Hoc Score</span>
            </div>

            <div style={{ marginBottom: 16 }}>
              <LocationInput onSelect={handleAdHoc} />
            </div>

            {adhocResult && adhocWeather && (
              <div
                className="demo-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "300px 1fr",
                  gap: 14,
                  alignItems: "start",
                }}
              >
                <ScoreCard result={adhocResult} weather={adhocWeather}
                  wxSource={adhocWxSource} locLabel={adhocLabel} dateStr={dateStr} />
                <RecsPanel sif={adhocResult.sif} recs={adhocResult.recs} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
